import React, { useState } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import TripComponentForm from './TripComponentForm';
import EditChauffeuredServiceModal from './EditChauffeuredServiceModal';
import BundleComponentForm from './BundleComponentForm';

const EditComponentModal = ({ tripId, component, onClose, onSuccess, tripExecutives, meetingExecutives, allExecutives = [], allContacts = [], tripComponents = [] }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');

  const handleComponentUpdate = async (componentData) => {
    setIsUpdating(true);
    setUpdateError('');

    try {
      const apiBase = getApiBase();
      await axios.put(`${apiBase}/trips/${tripId}/components/${component.id}`, componentData, {
        headers: getAuthHeaders()
      });
      onSuccess();
    } catch (err) {
      console.error('Component update failed:', err.response?.status, err.response?.data?.detail || err.message);
      
      let errorMessage = 'Failed to update component';
      
      if (err.response?.data?.detail) {
        // Backend error detail
        errorMessage += ': ' + err.response.data.detail;
      } else if (err.response?.data?.message) {
        // Backend error message
        errorMessage += ': ' + err.response.data.message;
      } else if (err.message) {
        // Network or other error
        errorMessage += ': ' + err.message;
      } else if (typeof err === 'string') {
        errorMessage += ': ' + err;
      } else {
        // Fallback for objects
        errorMessage += ': ' + JSON.stringify(err);
      }

      setUpdateError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  // Use specialized edit modal for chauffeur services
  if (component.component_type === 'chauffeur_service') {
    return (
      <EditChauffeuredServiceModal
        tripId={tripId}
        component={component}
        tripExecutives={tripExecutives}
        allExecutives={allExecutives}
        allContacts={allContacts}
        onSuccess={onSuccess}
        onClose={onClose}
      />
    );
  }

  // Use bundle form for bundles
  if (component.component_type === 'bundle') {
    return (
      <div className="fixed inset-0 bg-ui-ink bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
        <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-ui-surface max-h-[95vh] overflow-y-auto">
          <div className="mt-3">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1" />
              <button onClick={onClose} className="text-ui-muted hover:text-ui-ink-3 text-xl">×</button>
            </div>
            <BundleComponentForm
              tripId={tripId}
              component={component}
              tripComponents={tripComponents}
              onSuccess={onSuccess}
              onCancel={onClose}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-ui-ink bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
      <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-ui-surface min-h-[50vh] max-h-[95vh] overflow-y-auto">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1">
              {/* Title and subtitle removed */}
            </div>
            <button
              onClick={onClose}
              className="text-ui-muted hover:text-ui-ink-3 text-xl"
            >
              ×
            </button>
          </div>

          {/* Display update error if any */}
          {updateError && (
            <div className="mb-4 p-4 bg-ui-red-bg border border-ui-red-line rounded-xl">
              <div className="flex items-center gap-2">
                <div className="text-ui-red-fg font-medium">Update Error</div>
              </div>
              <div className="text-ui-red-fg text-sm mt-1">{updateError}</div>
            </div>
          )}

          <TripComponentForm
            tripId={tripId}
            componentType={component.component_type}
            onSubmit={handleComponentUpdate}
            onCancel={onClose}
            tripExecutives={tripExecutives}
            meetingExecutives={meetingExecutives}
            component={component}
            allContacts={allContacts}
          />
        </div>
      </div>
    </div>
  );
};

export default EditComponentModal;